import { useState, useEffect, useRef, useCallback } from 'react'
import { consumer } from '../lib/actionCable'

/**
 * Subscribes to ChatsChannel for a conversation.
 * Accumulates streamed chunks by order and signals completion.
 *
 * @param {string|null} chatId - Conversation ID to subscribe to
 * @returns {{ streamingMessage: { text: string, complete: boolean } | null, clearStream: Function }}
 */
export function useCharacterChatStream(chatId) {
  const [streamingMessage, setStreamingMessage] = useState(null)
  const subscriptionRef = useRef(null)
  const chunksRef = useRef({})

  useEffect(() => {
    if (!chatId) return

    const subscription = consumer.subscriptions.create(
      { channel: 'ChatsChannel', room: chatId },
      {
        received(data) {
          const { body, complete, order } = data

          // Store chunk by order number
          chunksRef.current[order] = body || ''

          // Rebuild text from contiguous chunks starting at 0
          let text = ''
          for (let i = 0; ; i++) {
            if (!(i in chunksRef.current)) break
            text += chunksRef.current[i]
          }

          setStreamingMessage({ text, complete: !!complete })
        },
      }
    )

    subscriptionRef.current = subscription

    return () => {
      subscription.unsubscribe()
      subscriptionRef.current = null
      chunksRef.current = {}
    }
  }, [chatId])

  const clearStream = useCallback(() => {
    chunksRef.current = {}
    setStreamingMessage(null)
  }, [])

  return { streamingMessage, clearStream }
}
